import { useCallback, useEffect, useState } from "react";
import client from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { toast } from "sonner";
import { MapPin, Link2, X } from "lucide-react";

// Unmatched E911 / AML vendor locations that didn't match an open call — one-click attach to a CAD# / Case #.
export const E911Queue = ({ incidents = [], onPick }) => {
  const [rows, setRows] = useState([]);
  const [pick, setPick] = useState({});
  const load = useCallback(() => client.get("/e911/unmatched").then((r) => setRows(r.data.rows || [])).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useRealtime(["incidents"], load);
  if (!rows.length) return null;
  const attach = async (r) => {
    const cc = pick[r.ts];
    if (!cc) { toast.error("Pick a call first"); return; }
    try { await client.post("/e911/unmatched/attach", null, { params: { cc_number: cc, ts: r.ts } }); toast.success(`E911 location attached to ${cc}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Attach failed"); }
  };
  const dismiss = async (r) => { try { await client.post("/e911/unmatched/dismiss", null, { params: { ts: r.ts } }); load(); } catch { toast.error("Dismiss failed"); } };
  return (
    <div data-testid="e911-unmatched-queue" className="border-b border-[#F59E0B]/50 bg-[#F59E0B]/10 px-3 py-1.5">
      <div className="font-head text-[10px] font-bold text-[#FCD34D] flex items-center gap-1.5"><MapPin size={11} /> UNMATCHED E911 LOCATIONS ({rows.length}) · vendor fixes with no open call — attach or dismiss</div>
      <div className="mt-1 space-y-1">
        {rows.map((r) => (
          <div key={r.ts} data-testid={`e911-unmatched-${r.ts}`} className="flex flex-wrap items-center gap-2 font-mono text-[10px] text-[#FDE68A]">
            <span className="font-bold uppercase">{r.source || "aml"}</span>
            <span>{r.phone || "no phone"}</span>
            <a className="underline" href={`https://www.google.com/maps?q=${r.lat},${r.lng}`} target="_blank" rel="noreferrer">{r.lat?.toFixed(5)}, {r.lng?.toFixed(5)}{r.accuracy_m ? ` ±${Math.round(r.accuracy_m)}m` : ""}</a>
            <span className="text-[#9CA3AF]">{new Date(r.ts).toLocaleTimeString("en-GB", { hour12: false }).slice(0, 5)}</span>
            <select data-testid={`e911-pick-${r.ts}`} value={pick[r.ts] || ""} onChange={(e) => setPick((p) => ({ ...p, [r.ts]: e.target.value }))}
              className="bg-[#141518] border border-[#2C2D35] text-white px-1 py-0.5 text-[10px] max-w-[260px]">
              <option value="">— attach to call —</option>
              {incidents.map((i) => <option key={i.id} value={i.cc_number}>{i.public_ref ? `CAD# ${i.public_ref} · ` : ""}{i.cc_number} · {i.call_type_label} · {i.location?.address?.slice(0, 30)}</option>)}
            </select>
            <button data-testid={`e911-attach-${r.ts}`} onClick={() => attach(r)} className="px-2 py-0.5 text-[9px] font-head font-bold bg-[#10B981] text-black rounded-sm flex items-center gap-1"><Link2 size={9} /> ATTACH</button>
            {onPick && <button onClick={() => onPick(r)} className="px-2 py-0.5 text-[9px] font-head font-bold border border-[#2C2D35] text-white rounded-sm">NEW CALL HERE</button>}
            <button data-testid={`e911-dismiss-${r.ts}`} onClick={() => dismiss(r)} title="Dismiss" className="text-[#9CA3AF] hover:text-white"><X size={11} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
